import React, { useState } from 'react';
import { CheckPicker, Checkbox, CheckboxGroup, Input, InputNumber, Message, SelectPicker } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showToast } from '../../utils/toast';
import {
  useDeleteTimesheetRule,
  useReorderTimesheetRules,
  useSaveTimesheetRule,
  useTimesheetRules,
  useTimesheetTasks,
} from '../../hooks/useTimesheet';
import { Banner, Switch, TaskPicker } from './TimesheetParts';

/**
 * Admin > Configuration > Timesheet > Rules: the checks every timesheet (and
 * View Hours) runs, top to bottom. Drag a card to reorder, flip its switch to
 * turn it off, click it to edit. "Add rule" picks a kind of rule first, then
 * opens the same editor.
 *
 * Every choice the editor offers comes from the server's catalog
 * (server/config/timesheetRules.js), so the two cannot drift apart.
 */

const options = (list) => (list || []).map((item) => ({ value: item.key, label: item.label }));

// What a brand new rule of each kind starts as. The server checks the lot
// again before saving (normalizeRule in services/timesheetRules.js).
const NEW_CONFIG = {
  duration: { taskIds: [], excludeTypes: [], op: 'divisible_by', value: 15, unit: 'minutes', toleranceSeconds: 0, display: 'list' },
  description: { taskIds: [], excludeTypes: [], op: 'has_value', value: '', display: 'list' },
  break: {
    type: 'meal_break', check: 'count', requirement: 'every', everyHours: 5, afterHours: [2, 6], measure: 'count', minutesEach: 30,
    op: 'equal_to', value: 0, unit: 'minutes', display: 'list',
  },
  overtime: { taskIds: [], excludeTypes: [], measure: 'total', period: 'day', op: 'at_most', threshold: 'hours', hours: 8 },
  close_date: { checks: ['expected_hours', 'nothing_else', 'only_on_close_dates'] },
  time_off: { type: 'paid_time_off' },
  task: { check: 'lowest_level', taskIds: [], excludeTypes: [], display: 'list' },
  manual: { question: '', onlyWhenFailing: null },
};

const newDraft = (category, catalog) => ({
  id: null,
  category,
  name: `${catalog.categories.find((c) => c.key === category)?.label || 'New'} rule`,
  description: '',
  appliesTo: 'everyone',
  severity: 'fail',
  enabled: true,
  config: { ...NEW_CONFIG[category] },
});

const draftOf = (rule) => ({
  id: rule.id,
  category: rule.category,
  name: rule.name,
  description: rule.description || '',
  appliesTo: rule.appliesTo,
  severity: rule.severity,
  enabled: rule.enabled,
  // Every field a category can use, so switching a break rule from "count" to
  // "duration" has something to show.
  config: { ...NEW_CONFIG[rule.category], ...rule.config },
});

// One line of a rule's sentence: "If project/task is [...]", "ensure [...]".
function RuleRow({ label, children }) {
  return (
    <div className="ts-rule-row">
      <span className="ts-rule-row__label">{label}</span>
      <div className="ts-rule-row__controls">{children}</div>
    </div>
  );
}

function Segment({ value, onChange, choices, label }) {
  return (
    <div className="bdc-segment" role="group" aria-label={label}>
      {choices.map((choice) => (
        <button
          key={choice.key}
          type="button"
          className={`bdc-btn bdc-btn--sm${value === choice.key ? ' bdc-btn--primary' : ''}`}
          aria-pressed={value === choice.key}
          onClick={() => onChange(choice.key)}
        >
          {choice.label}
        </button>
      ))}
    </div>
  );
}

// "If project/task is [any / these]" and "except [types]". The tasks are
// picked from the same tree the timesheet's entry bar uses, without the
// favorites; a picked project or task covers everything under it.
function ScopeRows({ config, set, tasks, typeOptions }) {
  return (
    <>
      <RuleRow label="If project/task is">
        <div style={{ flex: 1, minWidth: 260 }}>
          <TaskPicker
            tasks={tasks}
            value={config.taskIds || []}
            onChange={(taskIds) => set({ taskIds: taskIds || [] })}
            multiple
            favorites={false}
            placeholder="Any project or task"
          />
        </div>
      </RuleRow>
      <RuleRow label="except type">
        <CheckPicker
          data={typeOptions}
          value={config.excludeTypes || []}
          onChange={(excludeTypes) => set({ excludeTypes: excludeTypes || [] })}
          placeholder="No exceptions"
          searchable={false}
          block
          preventOverflow
          style={{ minWidth: 220 }}
        />
      </RuleRow>
    </>
  );
}

// "[divisible by] [15] [minutes]", plus the timer tolerance for divisibility.
function DurationControls({ config, set, catalog }) {
  return (
    <>
      <SelectPicker data={options(catalog.durationOps)} value={config.op} onChange={(op) => set({ op })} cleanable={false} searchable={false} style={{ width: 150 }} />
      <InputNumber value={config.value ?? ''} onChange={(value) => set({ value: value === '' ? '' : Number(value) })} min={0} style={{ width: 100 }} />
      <SelectPicker data={options(catalog.durationUnits)} value={config.unit} onChange={(unit) => set({ unit })} cleanable={false} searchable={false} style={{ width: 120 }} />
    </>
  );
}

function DescriptionControls({ config, set, catalog }) {
  const needsValue = catalog.descriptionOps.find((o) => o.key === config.op)?.needsValue;
  return (
    <>
      <SelectPicker data={options(catalog.descriptionOps)} value={config.op} onChange={(op) => set({ op })} cleanable={false} searchable={false} style={{ width: 190 }} />
      {needsValue ? (
        <Input value={config.value || ''} onChange={(value) => set({ value })} placeholder="text" style={{ width: 200 }} />
      ) : null}
    </>
  );
}

function DisplayRow({ config, set, catalog }) {
  return (
    <RuleRow label="Show it">
      <SelectPicker data={options(catalog.displays)} value={config.display || 'list'} onChange={(display) => set({ display })} cleanable={false} searchable={false} style={{ width: 300 }} />
    </RuleRow>
  );
}

// The settings for one kind of rule, as the sentence it reads as.
function CategoryFields({ draft, setConfig, catalog, tasks, typeOptions, otherRules }) {
  const c = draft.config;
  const set = setConfig;
  switch (draft.category) {
    case 'duration':
      return (
        <>
          <ScopeRows config={c} set={set} tasks={tasks} typeOptions={typeOptions} />
          <RuleRow label="ensure logged duration is">
            <DurationControls config={c} set={set} catalog={catalog} />
          </RuleRow>
          {c.op === 'divisible_by' ? (
            <RuleRow label="only warn within">
              <InputNumber value={c.toleranceSeconds || 0} onChange={(v) => set({ toleranceSeconds: Number(v) || 0 })} min={0} max={3600} style={{ width: 100 }} />
              <span className="ts-rule-row__unit">seconds of a step (a timer stopped a beat off)</span>
            </RuleRow>
          ) : null}
          <DisplayRow config={c} set={set} catalog={catalog} />
        </>
      );
    case 'description':
      return (
        <>
          <ScopeRows config={c} set={set} tasks={tasks} typeOptions={typeOptions} />
          <RuleRow label="ensure description">
            <DescriptionControls config={c} set={set} catalog={catalog} />
          </RuleRow>
          <DisplayRow config={c} set={set} catalog={catalog} />
        </>
      );
    case 'break':
      return (
        <>
          <RuleRow label="If type is">
            <SelectPicker data={typeOptions} value={c.type} onChange={(type) => set({ type })} cleanable={false} searchable={false} style={{ width: 220 }} />
          </RuleRow>
          <RuleRow label="ensure">
            <SelectPicker data={options(catalog.breakChecks)} value={c.check} onChange={(check) => set({ check })} cleanable={false} searchable={false} style={{ width: 280 }} />
          </RuleRow>
          {c.check === 'count' ? (
            <>
              <RuleRow label="owed">
                <SelectPicker data={options(catalog.breakRequirements)} value={c.requirement} onChange={(requirement) => set({ requirement })} cleanable={false} searchable={false} style={{ width: 290 }} />
                {c.requirement === 'every' ? (
                  <>
                    <InputNumber value={c.everyHours ?? ''} onChange={(v) => set({ everyHours: v === '' ? '' : Number(v) })} min={0} step={0.5} style={{ width: 90 }} />
                    <span className="ts-rule-row__unit">hours</span>
                  </>
                ) : (
                  <>
                    <Input
                      value={Array.isArray(c.afterHours) ? c.afterHours.join(', ') : c.afterHours || ''}
                      onChange={(afterHours) => set({ afterHours })}
                      placeholder="2, 6"
                      style={{ width: 110 }}
                    />
                    <span className="ts-rule-row__unit">hours</span>
                  </>
                )}
              </RuleRow>
              <RuleRow label="counted by">
                <SelectPicker data={options(catalog.breakMeasures)} value={c.measure} onChange={(measure) => set({ measure })} cleanable={false} searchable={false} style={{ width: 290 }} />
              </RuleRow>
              <RuleRow label="each break is">
                <InputNumber value={c.minutesEach ?? ''} onChange={(v) => set({ minutesEach: v === '' ? '' : Number(v) })} min={0} style={{ width: 90 }} />
                <span className="ts-rule-row__unit">minutes</span>
              </RuleRow>
            </>
          ) : c.check === 'duration' ? (
            <>
              <RuleRow label="each one's duration is">
                <DurationControls config={c} set={set} catalog={catalog} />
              </RuleRow>
              <DisplayRow config={c} set={set} catalog={catalog} />
            </>
          ) : (
            <>
              <RuleRow label="each one's description">
                <DescriptionControls config={c} set={set} catalog={catalog} />
              </RuleRow>
              <DisplayRow config={c} set={set} catalog={catalog} />
            </>
          )}
        </>
      );
    case 'overtime':
      return (
        <>
          <ScopeRows config={c} set={set} tasks={tasks} typeOptions={typeOptions} />
          <RuleRow label="ensure">
            <SelectPicker data={options(catalog.overtimeMeasures)} value={c.measure} onChange={(measure) => set({ measure })} cleanable={false} searchable={false} style={{ width: 230 }} />
            <SelectPicker
              data={options(catalog.overtimePeriods)}
              value={c.period}
              onChange={(period) => set({ period, ...(period !== 'pay_period' && c.threshold === 'tawh' ? { threshold: 'hours' } : {}) })}
              cleanable={false}
              searchable={false}
              style={{ width: 160 }}
            />
          </RuleRow>
          <RuleRow label="is">
            <SelectPicker data={options(catalog.comparisons)} value={c.op} onChange={(op) => set({ op })} cleanable={false} searchable={false} style={{ width: 150 }} />
            <SelectPicker
              data={options(catalog.overtimeThresholds).filter((o) => c.period === 'pay_period' || o.value !== 'tawh')}
              value={c.threshold}
              onChange={(threshold) => set({ threshold })}
              cleanable={false}
              searchable={false}
              style={{ width: 200 }}
            />
            {c.threshold === 'hours' ? (
              <>
                <InputNumber value={c.hours ?? ''} onChange={(v) => set({ hours: v === '' ? '' : Number(v) })} min={0} style={{ width: 90 }} />
                <span className="ts-rule-row__unit">hours</span>
              </>
            ) : null}
          </RuleRow>
        </>
      );
    case 'close_date':
      return (
        <RuleRow label="ensure">
          <CheckboxGroup value={c.checks || []} onChange={(checks) => set({ checks })}>
            {catalog.closeDateChecks.map((check) => <Checkbox key={check.key} value={check.key}>{check.label}</Checkbox>)}
          </CheckboxGroup>
        </RuleRow>
      );
    case 'time_off':
      return (
        <RuleRow label="logged against type">
          <SelectPicker data={typeOptions} value={c.type} onChange={(type) => set({ type })} cleanable={false} searchable={false} style={{ width: 220 }} />
        </RuleRow>
      );
    case 'task':
      return (
        <>
          <ScopeRows config={c} set={set} tasks={tasks} typeOptions={typeOptions} />
          <RuleRow label="ensure">
            <SelectPicker data={options(catalog.taskChecks)} value={c.check} onChange={(check) => set({ check })} cleanable={false} searchable={false} style={{ width: 360 }} />
          </RuleRow>
          <DisplayRow config={c} set={set} catalog={catalog} />
        </>
      );
    case 'manual':
      return (
        <>
          <RuleRow label="Reviewer is asked">
            <Input as="textarea" rows={3} value={c.question || ''} onChange={(question) => set({ question })} placeholder="e.g. Do the notes make sense for the selected task?" />
          </RuleRow>
          <RuleRow label="only when this fails">
            <SelectPicker
              data={otherRules.map((r) => ({ value: r.key, label: r.name }))}
              value={c.onlyWhenFailing || null}
              onChange={(onlyWhenFailing) => set({ onlyWhenFailing: onlyWhenFailing || null })}
              placeholder="Always ask"
              block
              preventOverflow
              style={{ minWidth: 260 }}
            />
          </RuleRow>
        </>
      );
    default:
      return null;
  }
}

// Add a rule (pick its kind first) or edit one. Both steps render the same Modal
// at the same place, so moving from one to the other is one dialog, not two.
function RuleModal({ open, start, catalog, tasks, rules, onClose }) {
  const saveMutation = useSaveTimesheetRule();
  const deleteMutation = useDeleteTimesheetRule();
  const [draft, setDraft] = useState(start.rule ? draftOf(start.rule) : null);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const typeOptions = options(catalog.types);
  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setConfig = (patch) => setDraft((prev) => ({ ...prev, config: { ...prev.config, ...patch } }));
  const category = draft ? catalog.categories.find((c) => c.key === draft.category) : null;

  const save = async () => {
    setError(null);
    try {
      await saveMutation.mutateAsync({
        id: draft.id || undefined,
        category: draft.id ? undefined : draft.category,
        name: draft.name,
        description: draft.description,
        appliesTo: draft.appliesTo,
        severity: draft.severity,
        enabled: draft.enabled,
        config: draft.config,
      });
      showToast('success', draft.id ? 'Rule saved.' : 'Rule added.');
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  const remove = async () => {
    try {
      await deleteMutation.mutateAsync(draft.id);
      showToast('success', 'Rule deleted.');
      onClose();
    } catch (err) {
      setError(String(err?.message || err));
    }
  };

  // Step one for a new rule: which kind.
  if (!draft) {
    return (
      <Modal open={open} onClose={onClose} size="md">
        <Modal.Header><Modal.Title>Add a Rule</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="ts-rule-kinds">
            {catalog.categories.map((c) => (
              <button key={c.key} type="button" className="ts-rule-kind" onClick={() => setDraft(newDraft(c.key, catalog))}>
                <i className={`bi ${c.icon}`} aria-hidden="true" />
                <span className="ts-rule-kind__label">{c.label}</span>
                <span className="ts-rule-kind__help">{c.help}</span>
              </button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>
          <i className={`bi ${category?.icon} me-2`} aria-hidden="true" />
          {draft.id ? 'Edit' : 'New'} {category?.label} Rule
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error ? <Message type="error" showIcon className="mb-3">{error}</Message> : null}
        <div className="ts-rule-form">
          <RuleRow label="Name">
            <Input value={draft.name} onChange={(name) => set({ name })} autoFocus />
          </RuleRow>

          <div className="ts-rule-sentence">
            <CategoryFields
              draft={draft}
              setConfig={setConfig}
              catalog={catalog}
              tasks={tasks}
              typeOptions={typeOptions}
              otherRules={rules.filter((r) => r.category !== 'manual' && r.id !== draft.id)}
            />
          </div>

          <RuleRow label="Applies to">
            <Segment value={draft.appliesTo} onChange={(appliesTo) => set({ appliesTo })} choices={catalog.appliesTo} label="Applies to" />
          </RuleRow>
          {draft.category !== 'manual' ? (
            <RuleRow label="When it fails">
              <Segment value={draft.severity} onChange={(severity) => set({ severity })} choices={catalog.severities} label="Severity" />
            </RuleRow>
          ) : null}
          <RuleRow label="Description">
            <Input
              as="textarea"
              rows={2}
              value={draft.description}
              onChange={(description) => set({ description })}
              placeholder={start.rule?.summary || 'Written from the settings above'}
            />
          </RuleRow>
          <RuleRow label="">
            <Switch checked={draft.enabled} onChange={(enabled) => set({ enabled })} label="Turned on" />
          </RuleRow>
        </div>

        {draft.id ? (
          <section className="ts-taskmodal__manage" aria-label="Manage this rule">
            <div className="ts-taskmodal__manage-title">This rule</div>
            <div className="ts-taskmodal__manage-actions">
              {confirmDelete ? (
                <Button size="sm" variant="danger-solid" onClick={remove} loading={deleteMutation.isPending}>Confirm delete</Button>
              ) : (
                <Button size="sm" variant="danger" icon="trash3" onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )}
            </div>
          </section>
        ) : null}
      </Modal.Body>
      <Modal.Footer
        left={!draft.id && !start.rule ? (
          <Button icon="arrow-left" onClick={() => setDraft(null)}>
            Another kind
          </Button>
        ) : null}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!draft.name.trim()} loading={saveMutation.isPending}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
}

// One rule in the list. The grip drags; anywhere else opens the editor.
function RuleCard({ rule, catalog, canEdit, onOpen, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id || rule.key, disabled: !canEdit });
  const category = catalog.categories.find((c) => c.key === rule.category);
  const appliesTo = catalog.appliesTo.find((a) => a.key === rule.appliesTo);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`ts-rule${rule.enabled ? '' : ' ts-rule--off'}${isDragging ? ' ts-rule--dragging' : ''}`}
    >
      <button
        type="button"
        className="ts-rule__grip"
        aria-label={`Drag to reorder ${rule.name}`}
        disabled={!canEdit}
        {...attributes}
        {...listeners}
      >
        <i className="bi bi-grip-vertical" aria-hidden="true" />
      </button>
      <button type="button" className="ts-rule__body" onClick={onOpen} disabled={!canEdit}>
        <span className="ts-rule__head">
          <i className={`bi ${category?.icon} ts-rule__icon`} aria-hidden="true" />
          <span className="ts-rule__name">{rule.name}</span>
          <span className="ts-pill">{category?.label}</span>
          {rule.appliesTo !== 'everyone' ? <span className="ts-pill ts-pill--info">{appliesTo?.label} only</span> : null}
          {rule.severity === 'warn' && rule.category !== 'manual' ? <span className="ts-pill ts-pill--warn">Warning</span> : null}
          {!rule.enabled ? <span className="ts-pill">Off</span> : null}
        </span>
        <span className="ts-rule__summary">{rule.description || rule.summary}</span>
      </button>
      <div className="ts-rule__switch">
        <Switch checked={rule.enabled} onChange={onToggle} label="" disabled={!canEdit} />
      </div>
    </div>
  );
}

export default function RulesTab() {
  const rulesQuery = useTimesheetRules();
  const tasksQuery = useTimesheetTasks({ includeArchived: false });
  const reorder = useReorderTimesheetRules();
  const saveMutation = useSaveTimesheetRule();
  const [editing, setEditing] = useState(null);
  // A fresh editor each time one opens; the last one stays mounted while it fades out.
  const editingShown = useLastPresent(editing);
  const [editSession, setEditSession] = useState(0);
  const openEditor = (start) => {
    setEditSession((n) => n + 1);
    setEditing(start);
  };
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const data = rulesQuery.data;
  const rules = data?.rules || [];
  const catalog = data?.catalog;
  const installed = data?.installed !== false;
  const tasks = tasksQuery.data?.tasks || [];
  const ids = rules.map((r) => r.id || r.key);

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    reorder.mutate(next, { onError: (err) => showToast('error', String(err?.message || err)) });
  };

  const toggle = (rule, enabled) => {
    saveMutation.mutate({ id: rule.id, enabled }, { onError: (err) => showToast('error', String(err?.message || err)) });
  };

  if (rulesQuery.error) return <Message type="error" showIcon>{String(rulesQuery.error?.message || rulesQuery.error)}</Message>;
  if (!catalog) return <div className="ts-empty"><i className="bi bi-hourglass-split" aria-hidden="true" />Loading rules…</div>;

  return (
    <div className="ts-rules">
      <div className="ts-rules__head">
        <div>
          <h3 className="ts-rules__title">Rules</h3>
          <p className="ts-rules__note">Checked on every timesheet and in View Hours, top to bottom. Drag to reorder.</p>
        </div>
        <button type="button" className="bdc-btn bdc-btn--primary" onClick={() => openEditor({})} disabled={!installed}>
          <i className="bi bi-plus-lg" aria-hidden="true" />
          Add rule
        </button>
      </div>

      {!installed ? (
        <Banner tone="warn" icon="bi-exclamation-triangle" title="These are the default rules">
          Run <code>server/sql/timesheet_rules.sql</code> to change them. Until then every timesheet is checked against these.
        </Banner>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="ts-rules__list">
            {rules.map((rule) => (
              <RuleCard
                key={rule.id || rule.key}
                rule={rule}
                catalog={catalog}
                canEdit={installed}
                onOpen={() => openEditor({ rule })}
                onToggle={(enabled) => toggle(rule, enabled)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {editingShown ? (
        <RuleModal
          key={editSession}
          open={Boolean(editing)}
          start={editingShown}
          catalog={catalog}
          tasks={tasks}
          rules={rules}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
